import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { to: '/', label: '首页', end: true },
  { to: '/category/movie', label: '电影', end: false },
  { to: '/category/tv', label: '电视剧', end: false },
  { to: '/news', label: '资讯', end: false },
  { to: '/about', label: '关于我们', end: false },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setMobileSearch(false);
  }, [location.pathname]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    if (!kw) return;
    navigate(`/category/all/filter?q=${encodeURIComponent(kw)}`);
    setKeyword('');
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
      isActive ? 'text-accent-400' : 'text-background-100 hover:text-accent-300'
    }`;

  return (
    <header className="sticky top-0 z-50 bg-primary-950/95 backdrop-blur border-b border-primary-800/60">
      <div className="container-page">
        <div className="flex h-16 items-center gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-primary-950">
              <i className="ri-movie-2-line text-xl"></i>
            </span>
            <span className="text-lg font-bold tracking-wide text-background-50 whitespace-nowrap">
              天堂<span className="text-accent-400">影视</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center ml-2">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <form onSubmit={handleSearch} className="hidden md:flex flex-1 justify-end">
            <div className="relative w-full max-w-xs">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索影片名称 / 导演 / 演员"
                className="w-full rounded-full bg-primary-900/70 border border-primary-700/60 py-2 pl-9 pr-3 text-sm text-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400"
              />
            </div>
          </form>

          <div className="flex items-center gap-1 md:hidden ml-auto">
            <button
              type="button"
              aria-label="搜索"
              onClick={() => {
                setMobileSearch((v) => !v);
                setTimeout(() => inputRef.current?.focus(), 50);
              }}
              className="flex h-9 w-9 items-center justify-center rounded-md text-background-100 hover:bg-primary-800 cursor-pointer"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-md text-background-100 hover:bg-primary-800 cursor-pointer"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
            </button>
          </div>
        </div>

        {mobileSearch && (
          <form onSubmit={handleSearch} className="md:hidden pb-3">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
              <input
                ref={inputRef}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索影片名称 / 导演 / 演员"
                className="w-full rounded-full bg-primary-900/70 border border-primary-700/60 py-2 pl-9 pr-3 text-sm text-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400"
              />
            </div>
          </form>
        )}
      </div>

      {menuOpen && (
        <nav className="md:hidden border-t border-primary-800/60 bg-primary-950/98 backdrop-blur">
          <div className="container-page py-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-3 text-sm font-medium cursor-pointer ${
                    isActive
                      ? 'bg-primary-900 text-accent-400'
                      : 'text-background-100 hover:bg-primary-900 hover:text-accent-300'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}